import { atomFamily } from "jotai-family";
import { atomWithDefault, loadable } from "jotai/utils";
import { isEqual } from "lodash-es";
import type { CustomTemplates } from "../../../pages/create-content/use-custom-templates";
import { toSlug } from "../../../utils/create-slug";
import type { FontSelectionSummary } from "../../brand-kit/hooks/use-font-selections";
import type { BrandTone } from "../../brand-kit/server/brand-tone-service";
import { debugLabel } from "../../generate/store";
import type { AccountContext } from "../../generic/hooks/use-account-context";
import { containsImageStyle } from "../contains-image-style";
import type { AugmentedTemplate } from "./custom-templates-atom";
import { multiformatAtom } from "./multiformat-atom";
import { previewUrlFamily } from "./preview-url-family";
import {
  type MultiformatImage,
  type MultiformatImageOrientation,
  type MultiformatImageType,
  type MultiformatLayout,
} from "./types";

export type ImageUsage = {
  photo: { landscape: number; portrait: number };
  icon: number;
  video: number;
};

export const previewsFamily = atomFamily(
  ({
    contentTypeName,
    answer,
    customTemplates,
    useSelectedLogo,
    fontSelections,
    db,
    fileId,
    imageStyles,
    brandTones,
    tone,
    context,
  }: {
    contentTypeName: string;
    answer: string;
    customTemplates: CustomTemplates | null;
    useSelectedLogo: boolean;
    fontSelections: FontSelectionSummary[];
    db?: string;
    fileId?: string;
    imageStyles?: string[];
    brandTones: BrandTone[];
    tone?: string;
    context?: AccountContext;
  }) =>
    debugLabel(
      `previewsFamily(${contentTypeName}, ${answer})`,
      atomWithDefault((get) => {
        const multiformat = get(multiformatAtom);
        const contentType = multiformat?.data?.content_types.find(
          (c) => c.name === contentTypeName,
        );
        if (!contentType) return;

        const layouts: (MultiformatLayout | undefined)[] = contentType.layouts.map((l) =>
          containsImageStyle(l, imageStyles) ? l : undefined,
        );

        const ds = contentType.design_huddle?.dimensions;
        const dimensions = ds
          ? { width: ds.width ?? undefined, height: ds.height ?? undefined }
          : undefined;

        const formatId = toSlug(contentTypeName).replace(/-/g, "_");
        const templates = discardExcessCustomTemplates(customTemplates, contentTypeName, layouts);
        const customLayouts = templates.flatMap((customTemplate, layoutIndex) =>
          Array.from(
            {
              length: contentType.multipage
                ? 1
                : Math.min(customTemplate.page_count, layouts.length),
            },
            (_, i) => {
              const index = contentType.multipage ? layoutIndex : i;
              return (
                layouts[index] &&
                get(
                  loadable(
                    previewUrlFamily({
                      formatId,
                      answer,
                      imgNum: index + 1,
                      useSelectedLogo,
                      templateIndex: i + 1,
                      templateCode: customTemplate.template.template_code ?? "",
                      dimensions,
                      images: layouts[index].images,
                      multipage: contentType.multipage,
                      fontSelections,
                      imageUsage: countImagesUsed(layouts, 0),
                      customTemplate: true,
                      db,
                      fileId,
                      brandTones,
                      tone,
                      ...(context && { context }),
                    }),
                  ),
                )
              );
            },
          ),
        );

        const layoutsUsedCount = contentType.multipage
          ? templates.length
          : Math.min(templates[0] ? templates[0].page_count : 0, layouts.length);
        const imageUsage = countImagesUsed(layouts, layoutsUsedCount);
        return [
          ...customLayouts,
          ...layouts.map((layout, i) => {
            return (
              layout &&
              get(
                loadable(
                  previewUrlFamily({
                    formatId,
                    answer,
                    imgNum: i + 1,
                    useSelectedLogo,
                    templateIndex: i + 1,
                    templateCode: layout.design_huddle_id,
                    dimensions,
                    images: layout.images,
                    multipage: contentType.multipage,
                    fontSelections,
                    imageUsage,
                    customTemplate: false,
                    db,
                    fileId,
                    brandTones,
                    tone,
                    ...(context && { context }),
                  }),
                ),
              )
            );
          }),
        ];
      }),
    ),
  isEqual,
);

// removes custom templates we don't have layout configuration for
// only possible in multipage content types,
// as other types have one template with multiple pages,
// each representing a single preview
function discardExcessCustomTemplates(
  customTemplates: CustomTemplates | null,
  contentTypeName: string,
  layouts: (
    | {
        design_huddle_id: string;
        images: MultiformatImage[];
      }
    | undefined
  )[],
): AugmentedTemplate[] {
  const validTemplates = (customTemplates?.[contentTypeName] ?? []).filter(
    (template) => template.template.template_code,
  );
  if (!validTemplates) return [];

  return validTemplates.slice(0, layouts.length);
}

function countImagesUsed(
  layouts: (MultiformatLayout | undefined)[],
  layoutsUsedCount: number,
): ImageUsage {
  const layoutsUsed = layouts.slice(0, layoutsUsedCount);
  return {
    photo: {
      landscape:
        layoutsUsed && layoutsUsed.length > 0
          ? countImages(
              layoutsUsed.filter((l) => l !== undefined),
              "photo",
              "landscape",
            )
          : 0,
      portrait:
        layoutsUsed && layoutsUsed.length > 0
          ? countImages(
              layoutsUsed.filter((l) => l !== undefined),
              "photo",
              "portrait",
            )
          : 0,
    },
    icon:
      layoutsUsed && layoutsUsed.length > 0
        ? countImages(
            layoutsUsed.filter((l) => l !== undefined),
            "icon",
          )
        : 0,
    video:
      layoutsUsed && layoutsUsed.length > 0
        ? countImages(
            layoutsUsed.filter((l) => l !== undefined),
            "video",
          )
        : 0,
  };
}

function countImages(
  layouts: MultiformatLayout[],
  type: MultiformatImageType,
  orientation?: MultiformatImageOrientation,
): number {
  return layouts.reduce((count, layout) => {
    return (
      count +
      layout.images.filter((img) => img.type === type && img.orientation === orientation).length
    );
  }, 0);
}
